import React, { useId, useState } from 'react';
import useDeviceDetect from '../hooks/useDeviceDetect';
import { Scan, Power, PowerOff, Camera } from 'lucide-react';
import CameraScanner from './common/CameraScanner';
import { cameraSupported } from '../utils/barcodeDetector';
import './BarcodeScanner.css';

// Scan / manual-entry box with the page's single scanner on/off control.
//
// Props:
//   onScan(code)   called with the trimmed code on Enter or the Scan button
//   active         scanner on?
//   setActive      (bool) => void; without it the on/off controls are not shown
//   showToggle     show the on/off button in the header (default true). Modules that
//                  render their own toggle can pass false, but should rather drop theirs.
//   label          visible label for the input (default "Scan or type barcode")
//   title          heading text (default "Barcode Scanner"; pass null to hide it)
//   camera         'single' | 'continuous' to offer scanning with the device camera
//                  (single closes after one code; continuous keeps counting)
//
// While off, a full-width "SCANNER OFF" bar sticks to the top of the viewport (within
// the scanner's section) and turns it back on when tapped.
const BarcodeScanner = ({
  onScan,
  active,
  setActive,
  showToggle = true,
  label = 'Scan or type barcode',
  title = 'Barcode Scanner',
  camera = null,
}) => {
  const [barcode, setBarcode] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const showCamera = Boolean(camera) && cameraSupported();
  const inputId = useId();
  const { isMobile, isTablet, isIPad } = useDeviceDetect();
  const compact = isMobile || isTablet || isIPad;
  const canToggle = typeof setActive === 'function';

  const submit = () => {
    const code = barcode.trim();
    if (!code) return;
    onScan(code);
    setBarcode('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && barcode.trim()) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className={`barcode-scanner ${compact ? 'compact-layout' : ''} ${active ? '' : 'is-off'}`.trim()}>
      {!active && canToggle && (
        <button type="button" className="scanner-off-bar" onClick={() => setActive(true)}>
          <PowerOff size={20} aria-hidden="true" />
          <span><strong>SCANNER OFF</strong> — tap to turn on</span>
        </button>
      )}

      {(title || (showToggle && canToggle)) && (
        <div className="scanner-header">
          {title && <h2>{compact && title === 'Barcode Scanner' ? 'Scanner' : title}</h2>}
          {showToggle && canToggle && (
            <button
              type="button"
              role="switch"
              aria-checked={active}
              onClick={() => setActive(!active)}
              className={`scanner-toggle ${active ? 'active' : 'inactive'}`}
            >
              {active ? <Power size={18} aria-hidden="true" /> : <PowerOff size={18} aria-hidden="true" />}
              Scanner
              <span className="scanner-toggle-state" aria-hidden="true">{active ? 'ON' : 'OFF'}</span>
            </button>
          )}
        </div>
      )}

      <label className="scanner-label" htmlFor={inputId}>{label}</label>
      <div className="scanner-input-container">
        <input
          id={inputId}
          type="text"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!active}
          className="barcode-input"
          autoFocus={false} // Never auto-focus to prevent scrolling
          inputMode="text" // Barcodes can contain letters
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="go"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!active || !barcode.trim()}
          className="scan-button"
        >
          <Scan size={18} aria-hidden="true" /> {compact ? 'Scan' : 'Process Barcode'}
        </button>
        {showCamera && (
          <button
            type="button"
            onClick={() => setCameraOpen(true)}
            disabled={!active}
            className="scan-button camera-button"
            aria-label="Scan with camera"
          >
            <Camera size={20} aria-hidden="true" /> <span className="camera-label">{compact ? 'Scan with camera' : 'Camera'}</span>
          </button>
        )}
      </div>

      {showCamera && (
        <CameraScanner
          open={cameraOpen && active}
          continuous={camera === 'continuous'}
          onDetected={(code) => onScan(code)}
          onClose={() => setCameraOpen(false)}
        />
      )}

      {!compact && active && (
        <p className="scanner-instruction">
          Scan a barcode with your scanner device or type it above and press Enter.
        </p>
      )}
    </div>
  );
};

export default BarcodeScanner;
